import { useState, useEffect } from "react";
import { Sparkles, X, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { createPortal } from "react-dom";
import type { Diviner } from "@/lib/boide-api";

export function DivineRosterDialog({
  open,
  onOpenChange,
  onSelect,
  currentId,
  diviners,
  loading = false,
  error = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (id: string) => void;
  currentId?: string;
  diviners: Diviner[];
  loading?: boolean;
  error?: string | null;
}) {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 768px)");
    setIsMobile(media.matches);
    const listener = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev - 1 + diviners.length) % diviners.length);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev + 1) % diviners.length);
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      const idx = diviners.findIndex((d) => d.id === currentId);
      setActiveIndex(idx >= 0 ? idx : 0);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, currentId, diviners]);

  if (!open || !mounted) return null;

  return createPortal(
    <div className="divine-roster-overlay fixed inset-0 z-[11000] flex items-center justify-center backdrop-blur-md animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-cosmic opacity-25 pointer-events-none dark:opacity-60" />

      <button
        onClick={() => onOpenChange(false)}
        className="absolute top-6 right-6 z-50 p-3 rounded-full bg-background/30 hover:bg-background/60 border border-gold-soft/30 text-muted-foreground hover:text-foreground transition-all backdrop-blur-md"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="relative z-10 w-full h-full flex flex-col items-center py-6 sm:py-12 overflow-hidden">
        <div className="text-center mb-6 sm:mb-10 flex-shrink-0 animate-in slide-in-from-bottom-4 duration-500">
          <span className="font-display text-[11px] tracking-[0.4em] text-primary">
            THE DIVINE ROSTER
          </span>
          <h2 className="mt-3 font-serif-soft text-3xl sm:text-4xl italic text-gold-gradient drop-shadow-lg">
            Khởi Tâm Thỉnh Thần
          </h2>
          <p className="mt-2 text-sm text-muted-foreground/80 max-w-md mx-auto">
            Chọn một vị minh sư để dẫn dắt bạn trên con đường chiêm nghiệm số mệnh.
          </p>
        </div>

        {loading && (
          <div className="flex flex-col items-center gap-4 text-muted-foreground mt-16">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <p className="font-serif-soft italic text-sm">Đang thỉnh danh sách minh sư...</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center gap-4 text-destructive mt-16 max-w-sm text-center">
            <p className="font-serif-soft italic text-sm">Không thể tải danh sách thầy: {error}</p>
            <button
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-full border border-destructive/40 text-sm hover:bg-destructive/10 transition-colors"
            >
              Đóng
            </button>
          </div>
        )}

        {!loading && !error && diviners.length === 0 && (
          <div className="flex flex-col items-center gap-4 text-muted-foreground mt-16">
            <p className="font-serif-soft italic text-sm">Chưa có vị minh sư nào.</p>
          </div>
        )}

        {!loading && !error && diviners.length > 0 && (
          <div
            className={`relative w-full max-w-[1400px] flex-1 min-h-[400px] max-h-[70vh] flex items-stretch justify-center px-8 pb-8 animate-in zoom-in-95 duration-500 ${isMobile ? "gap-0" : "gap-4"}`}
          >
            {diviners.map((diviner, index) => {
              const isActive = index === activeIndex;
              const diff = index - activeIndex;
              const isLeft = diff < 0;

              let transform = "scale(1) translateY(0)";
              let zIndex = 10;
              let opacity = 1;
              let flexBasis = "20%";
              let filter = "brightness(1)";
              let display = "flex";

              if (isMobile) {
                if (isActive) {
                  transform = "scale(1) translateY(0)";
                  zIndex = 50;
                  flexBasis = "100%";
                  filter = "brightness(1.05)";
                  opacity = 1;
                } else {
                  display = "none";
                }
              } else {
                if (isActive) {
                  transform = "scale(1.02) translateY(-10px)";
                  zIndex = 50;
                  flexBasis = "36%";
                  filter = "brightness(1.1) drop-shadow(0 20px 40px rgba(212,175,55,0.25))";
                } else {
                  transform = `scale(0.95) translateX(${isLeft ? "-10px" : "10px"})`;
                  zIndex = 40 - Math.abs(diff);
                  opacity = 0.65;
                  flexBasis = "16%";
                  filter = "brightness(0.6) grayscale(20%)";
                }
              }

              return (
                <div
                  key={diviner.id}
                  onClick={() => setActiveIndex(index)}
                  className="relative h-full rounded-[2rem] overflow-hidden cursor-pointer transition-all duration-700 cubic-bezier(0.2, 0.8, 0.2, 1) flex-shrink-0 group"
                  style={{
                    flexBasis,
                    transform,
                    zIndex,
                    opacity,
                    filter,
                    display,
                    boxShadow: isActive
                      ? "0 0 0 1px rgba(212,175,55,0.4) inset"
                      : "0 0 0 1px rgba(255,255,255,0.1) inset",
                  }}
                >
                  {diviner.avatar ? (
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.5s] ease-out group-hover:scale-105"
                      style={{ backgroundImage: `url(${diviner.avatar})` }}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-b from-primary/20 to-background/80" />
                  )}

                  {/* Overlay inactive */}
                  <div
                    className={`absolute inset-0 transition-opacity duration-700 bg-gradient-to-t from-[#09050d] via-[#09050d]/70 to-transparent ${isActive ? "opacity-0" : "opacity-100"}`}
                  />

                  {/* Overlay active */}
                  <div
                    className={`absolute inset-0 transition-opacity duration-700 ${isActive ? "opacity-100" : "opacity-0"}`}
                    style={{
                      background:
                        "linear-gradient(to top, #09050d 0%, rgba(9,5,13,0.95) 20%, rgba(9,5,13,0.75) 40%, rgba(9,5,13,0.35) 60%, transparent 80%)",
                    }}
                  />

                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 flex flex-col items-center justify-end h-full text-center">
                    {/* Tên dọc — thẻ đóng */}
                    <div
                      className={`absolute bottom-12 transition-all duration-700 flex flex-col items-center gap-4 ${isActive ? "opacity-0 translate-y-8 pointer-events-none" : "opacity-100 translate-y-0"}`}
                    >
                      <h3
                        className="font-serif-soft italic text-2xl text-[#F0C978]/75 group-hover:text-[#FFD98A] transition-all duration-500 group-hover:drop-shadow-[0_0_8px_rgba(229,184,105,0.55)]"
                        style={{
                          writingMode: "vertical-rl",
                          textOrientation: "mixed",
                          letterSpacing: "0.45em",
                        }}
                      >
                        {diviner.ten}
                      </h3>
                      <div className="w-[1px] h-12 bg-[#F0C978]/45 group-hover:bg-[#FFD98A]/85 transition-colors duration-500" />
                    </div>

                    {/* Chi tiết — thẻ active */}
                    <div
                      className={`transition-all duration-700 flex flex-col items-center w-full ${isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12 pointer-events-none"}`}
                    >
                      {diviner.title && (
                        <div className="font-display text-[10px] tracking-[0.4em] text-[#EBC36F] mb-3 drop-shadow-[0_1px_8px_rgba(0,0,0,0.65)]">
                          {diviner.title.toUpperCase()}
                        </div>
                      )}

                      <h3 className="font-serif-soft italic text-3xl sm:text-5xl mb-3 sm:mb-4 text-[#D58B13] drop-shadow-[0_2px_14px_rgba(0,0,0,0.85)] dark:text-[#F3C76E]">
                        {diviner.ten}
                      </h3>

                      {diviner.mo_ta && (
                        <p className="text-[14px] sm:text-[15px] text-[#F3E8D3] leading-relaxed max-w-xs sm:max-w-sm mx-auto font-serif-soft mb-6 sm:mb-8 drop-shadow-[0_1px_10px_rgba(0,0,0,0.9)]">
                          {diviner.mo_ta}
                        </p>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(diviner.id);
                          onOpenChange(false);
                        }}
                        className="inline-flex items-center gap-3 rounded-full bg-gold-gradient px-7 sm:px-9 py-3 sm:py-3.5 text-xs sm:text-sm font-semibold text-[#0B0813] shadow-[0_8px_28px_-6px_rgba(229,184,105,0.55),0_0_0_1px_rgba(229,184,105,0.6)] transition-all hover:scale-[1.06] hover:shadow-[0_12px_36px_-6px_rgba(229,184,105,0.75),0_0_0_1px_rgba(229,184,105,0.9)] group/btn"
                      >
                        <span className="font-display tracking-[0.25em] uppercase">
                          Thỉnh Vị Này
                        </span>
                        <Sparkles className="h-4 w-4 transition-transform group-hover/btn:rotate-12" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {isMobile && diviners.length > 1 && (
              <>
                <button
                  onClick={handlePrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-50 p-2.5 rounded-full bg-[#09050d]/60 border border-[#F0C978]/35 text-[#F0C978] hover:bg-[#09050d]/80 transition-all backdrop-blur-md active:scale-95"
                  aria-label="Previous diviner"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-50 p-2.5 rounded-full bg-[#09050d]/60 border border-[#F0C978]/35 text-[#F0C978] hover:bg-[#09050d]/80 transition-all backdrop-blur-md active:scale-95"
                  aria-label="Next diviner"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
